import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { UserDto } from 'app/api/apiClient/generated/types';
import { getAdminPanelSession } from 'app/api/cookie';
import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import NavigationLink from 'app/components/layout/AppLayout/NavigationLink';
import { keys } from 'app/config/keys';
import {
  externalRoutes,
  factorRoutes,
  payeeRoutes,
  payorRoutes,
  routes,
} from 'app/constants/routes';
import { CompanyTypeId } from 'app/constants/user';
import { useIsNewDashboardEnabled } from 'app/hooks/admin/useIsNewDashboardEnabled';
import { useIsTriumphInternal } from 'app/state/AuthContext/hooks';
import { useGeneralState } from 'app/state/GeneralContext/hooks';

// Local Variables
const getUserDashboardLink = (user: null | UserDto): string => {
  if (!user?.company?.type.id) {
    return '/';
  }

  switch (user.company.type.id as CompanyTypeId) {
    case CompanyTypeId.Factor:
      return `/${routes.factor}/${factorRoutes.payors}`;
    case CompanyTypeId.Payee:
      return `/${routes.payee}/${payeeRoutes.dashboard}`;
    case CompanyTypeId.Payor:
      return `/${routes.payor}/${payorRoutes.dashboard}`;
    default:
      return '/';
  }
};

const getHref = ({
  isAdminSession,
  isNewDashboardEnabled,
  user,
}: {
  isAdminSession: string | undefined;
  isNewDashboardEnabled: boolean;
  user: null | UserDto;
}) => {
  if (isAdminSession) {
    return getUserDashboardLink(user);
  }

  if (isNewDashboardEnabled) {
    return `/${routes.admin}`;
  }

  return legacyAdminRoute;
};

const legacyAdminRoute = `${keys.CORE_URL}/${externalRoutes.admin}`;

// Component Definition
const AdminButton: FC = () => {
  const { t } = useTranslation('components');
  const {
    isSideNavOpen,
  } = useGeneralState();
  const user = useGetUser();
  const isNewDashboardEnabled = useIsNewDashboardEnabled();

  const isUserInternal = useIsTriumphInternal();

  if (!isUserInternal) {
    return null;
  }

  const isAdminSession = getAdminPanelSession();

  const linkPath = getHref({
    isAdminSession,
    isNewDashboardEnabled,
    user,
  });

  const href = linkPath.startsWith('http') ? linkPath : undefined;
  const to = !href ? linkPath : undefined;

  return (
    <NavigationLink
      activePaths={[]}
      href={href}
      iconName={isAdminSession ? 'exit' : 'shield'}
      id="admin-session-toggle"
      isSideNavOpen={isSideNavOpen}
      label={isAdminSession ? t('layouts.sideNav.exitToApp', 'Exit to App') : t('common:admin', 'Admin')}
      to={to}
    />
  );
};

export default AdminButton;
